// Screen awareness for every talking deity (SyQi included). The shell reports which screen
// and tool is showing; describeScreen() turns that into a short brief that rides along with
// every agent request (askAgent) and every live-voice session (useLiveVoice), so whoever is
// open always knows where the user is and what is on screen.
//
// On web the brief also carries the visible text of the active screen (read from the DOM node
// whose id = ScreenInfo.domId). Native has no DOM, so it gets the structured fields only.
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { formKeyForTool, getDeityForm } from '../olmps/deityFormRegistry';

export interface ScreenInfo {
  /** Human name of the screen, e.g. 'NOW (the orb)', 'Tool: KYRS'. */
  screen: string;
  /** ToolHost id of the open tool, when a tool is showing. */
  toolId?: string;
  toolName?: string;
  /** The tool's deity, when a tool is showing. */
  deityName?: string;
  gear?: string;
  /** Extra one-line detail (selected step, open sheet, ...). */
  detail?: string;
  /** Web: id of the DOM node holding the visible screen (RN `nativeID`). */
  domId?: string;
}

const MAX_SCREEN_TEXT = 3500;

let current: ScreenInfo = { screen: 'unknown' };
const screenListeners = new Set<(s: ScreenInfo) => void>();

export function setScreen(next: ScreenInfo): void {
  const same =
    next.screen === current.screen &&
    next.toolId === current.toolId &&
    next.detail === current.detail &&
    next.gear === current.gear &&
    next.domId === current.domId;
  current = next;
  if (!same) screenListeners.forEach((fn) => fn(next));
}

export function getScreen(): ScreenInfo {
  return current;
}

export function subscribeScreen(fn: (s: ScreenInfo) => void): () => void {
  screenListeners.add(fn);
  return () => {
    screenListeners.delete(fn);
  };
}

function visibleText(domId?: string): string {
  if (Platform.OS !== 'web' || !domId || typeof document === 'undefined') return '';
  const el = document.getElementById(domId) as HTMLElement | null;
  const raw = el?.innerText ?? '';
  const text = raw.replace(/[ \t]+/g, ' ').replace(/\n{2,}/g, '\n').trim();
  return text.length > MAX_SCREEN_TEXT ? `${text.slice(0, MAX_SCREEN_TEXT)}…` : text;
}

/** The open tool's form values, when that tool registered a deity form adapter. */
export function currentFormContext(): { formKey: string; currentValues: Record<string, unknown> } | undefined {
  const toolId = current.toolId;
  const formKey = toolId ? formKeyForTool(toolId) : undefined;
  const values = formKey ? getDeityForm(formKey)?.getCurrentValues() : undefined;
  return formKey && values ? { formKey, currentValues: values } : undefined;
}

/** Plain-text brief of what the user is looking at right now. */
export function describeScreen(): string {
  const s = current;
  const lines = [`Screen: ${s.screen}`];
  if (s.toolName) lines.push(`Open tool: ${s.toolName}${s.deityName ? ` (deity: ${s.deityName})` : ''}${s.gear ? `, gear ${s.gear}` : ''}`);
  if (s.detail) lines.push(s.detail);
  const form = currentFormContext();
  if (form) lines.push(`Open form "${form.formKey}" values: ${JSON.stringify(form.currentValues).slice(0, 1200)}`);
  const text = visibleText(s.domId);
  if (text) lines.push(`Visible on screen:\n${text}`);
  return lines.join('\n');
}

/** The brief wrapped for an agent request. */
export function screenPreamble(): string {
  return (
    `[APP CONTEXT, not from the user: what the user is looking at in OLMPS right now. ` +
    `Use it to answer questions about this screen, and act on this screen's records when asked.]\n` +
    `${describeScreen()}\n[END APP CONTEXT]\n\n`
  );
}

/** The user's LOCAL calendar date (YYYY-MM-DD): sent with agent requests so "today / tomorrow" resolve in their timezone. */
export function localDateKey(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ---- data changed: an agent wrote records, so the open tool should reload ----
let dataVersion = 0;
const dataListeners = new Set<(v: number) => void>();

export function notifyDataChanged(): void {
  dataVersion++;
  dataListeners.forEach((fn) => fn(dataVersion));
}

export function useDataVersion(): number {
  const [v, setV] = useState(dataVersion);
  useEffect(() => {
    dataListeners.add(setV);
    return () => {
      dataListeners.delete(setV);
    };
  }, []);
  return v;
}
